import { CommonModule } from '@angular/common';
import { Component, computed, input, output, ViewEncapsulation } from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxDivider } from '../divider/divider';
import { HxIconButton } from '../icon-button/icon-button';
import { HxTooltipDirective } from '../tooltip/tooltip';

/**
 * HxPageHeader - Reusable page-level header component
 *
 * Provides a consistent page header layout with optional back button, title, and flexible content areas. Uses
 * ng-content projection for maximum flexibility in displaying custom content.
 *
 * @example
 *   ```html
 *   <!-- Basic usage with title and back button (icon only) -->
 *   <hx-page-header
 *     [title]="'Primary Care Physician'"
 *     (backClick)="navigateBack()"
 *   />
 *
 *   <!-- With custom tooltip for back button -->
 *   <hx-page-header
 *     [title]="'Assessment Verification'"
 *     [backButtonTooltip]="'Return to previous page'"
 *     (backClick)="navigateBack()"
 *   />
 *
 *   <!-- With back button label -->
 *   <hx-page-header
 *     [showBackButtonLabel]="true"
 *     [backButtonLabel]="'Back to dashboard'"
 *     [title]="'Assessment Verification'"
 *     (backClick)="navigateBack()"
 *   />
 *
 *   <!-- With custom back icon and label -->
 *   <hx-page-header
 *     [showBackButtonLabel]="true"
 *     [backButtonLabel]="'Back'"
 *     [backButtonIcon]="'chevron-left'"
 *     [backButtonSize]="'lg'"
 *     [backButtonContext]="'neutral'"
 *     [title]="'Template Designer'"
 *     (backClick)="navigateBack()"
 *   />
 *
 *   <!-- With custom right content -->
 *   <hx-page-header [showBackButton]="true" [title]="'Assessment'">
 *     <div hxPageHeaderRight class="member-info">
 *       <span>Member: John Doe</span>
 *       <span>Age: 45</span>
 *     </div>
 *   </hx-page-header>
 *
 *   <!-- With custom content in all slots -->
 *   <hx-page-header>
 *     <button hxPageHeaderLeft hxIconButton variant="ghost">
 *       <hx-icon fontIcon="arrow-left" />
 *     </button>
 *     <h1 hxPageHeaderCenter>Custom Title</h1>
 *     <div hxPageHeaderRight>
 *       <button hxButton>Action</button>
 *     </div>
 *   </hx-page-header>
 *   ```;
 *
 * @accessibility
 * - WCAG 2.1 Level AA compliant
 * - Back button has proper aria-label
 * - Keyboard navigation: Tab to back button, Enter/Space to activate
 * - landmark role="banner" for page header
 */
@Component({
  selector: 'hx-page-header',
  standalone: true,
  imports: [CommonModule, HxDivider, HxIconButton, HxTooltipDirective],
  templateUrl: './page-header.html',
  styleUrls: ['./page-header.css'],
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'hx-page-header',
    '[class]': 'cssClass()',
    role: 'banner',
  },
})
export class HxPageHeader extends HxBaseControl {
  // Input Properties

  /** Label text for back button */
  backButtonLabel = input<string>('Back');

  /** Tooltip text for back button (shown on hover) */
  backButtonTooltip = input<string>('Go back');

  /** Whether to show the back button label text (icon always shows) */
  showBackButtonLabel = input<boolean>(false);

  /** Icon name for the back button */
  backButtonIcon = input<string>('arrow-left-circle');

  /** Size of the back button icon */
  backButtonSize = input<'xs' | 'sm' | 'md' | 'lg' | 'xl'>('md');

  /** Context/color scheme of the back button */
  backButtonContext = input<'brand' | 'neutral' | 'positive' | 'negative' | 'caution' | 'info'>('brand');

  /** Page title/heading text. Can be omitted if using custom center content. */
  title = input<string | undefined>(undefined);

  /** Optional subtitle text displayed below the title */
  subtitle = input<string | undefined>(undefined);

  // Output Events

  /** Emitted when back button is clicked */
  backClick = output<void>();

  // Computed Properties

  /** Computed aria-label for back button */
  backButtonAriaLabel = computed(() => {
    return this.backButtonLabel() || 'Go back';
  });

  // Methods

  onBackClick(): void {
    this.backClick.emit();
  }
}
